import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage-angular';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

/**
 * Robots de la cuenta con la que se ha iniciado sesión.
 * Se piden una sola vez por sesión (por token) y los comparten el menú, la creación
 * de tareas y las acciones. Lo normal es que la cuenta tenga un único robot.
 */
@Injectable({ providedIn: 'root' })
export class AccountRobotsService {
  private cache: { token: string; series: Promise<string[]> } | null = null;

  constructor(
    private http: HttpClient,
    private storage: Storage,
  ) {}

  // Números de serie de todos los robots de la cuenta
  async getSeries(): Promise<string[]> {
    const token: string | null = await this.storage.get('token');
    if (!token) return [];

    if (this.cache?.token !== token) this.cache = { token, series: this.fetchAll(token) };
    return this.cache.series;
  }

  // Serie del robot si la cuenta tiene exactamente uno; null si tiene varios o no se han podido cargar
  async getSingleRobot(): Promise<string | null> {
    const series = await this.getSeries();
    return series.length === 1 ? series[0] : null;
  }

  // Al cerrar sesión, para que la siguiente cuenta vuelva a pedirlos
  clear() {
    this.cache = null;
  }

  private async fetchAll(token: string): Promise<string[]> {
    const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
    const series: string[] = [];
    let page = 1;
    let totalPages = 1;

    try {
      do {
        const response: any = await firstValueFrom(
          this.http.get(`${environment.apiURL}robots?page=${page}`, { headers }),
        );
        series.push(...(response?.data ?? []).map((robot: any) => robot.serie as string));
        totalPages = response?.metadata?.totalPages ?? 1;
        page++;
      } while (page <= totalPages);
    } catch (error) {
      console.error(error);
      // No se guarda el fallo para que el siguiente intento vuelva a pedirlos
      this.cache = null;
      return [];
    }

    return series;
  }
}
